/* eslint-disable */
"use strict";

// ---------------------------------------------------------------------------
// Storefront API configuration
// ---------------------------------------------------------------------------
// Token is sourced from theme settings (Customize theme → Theme settings →
// Store Locator → Storefront API access token) and exposed to JS via
// snippets/js-variables.liquid → window.themeVariables.settings.storefrontApiToken.
//
// Must be a PUBLIC Storefront API access token (safe to expose, rate-limited,
// scoped). Required scopes:
//   - unauthenticated_read_product_pickup_locations
//   - unauthenticated_read_product_inventory
const STOREFRONT_API_VERSION = "2026-04";

function getStorefrontAccessToken() {
    return (
        (window.themeVariables &&
            window.themeVariables.settings &&
            window.themeVariables.settings.storefrontApiToken) ||
        ""
    );
}

function productData() {
    return {
        productEl: null,
        connectorPricing: [],
        productType: {},
        productLocations: [],
        productAvailabilities: [],
        storeLocation: {},
        productWarehouse: {},
        productAvailable: false,
        selectedOptions: "",
        storePrice: {},
        storePriceWithCompareAtPrice: false,
        productBadges: {},
        cartPrice: "",
        isBikeInCart: null,
        load: false,
        optionAvailabilityCache: {},
        availabilityLoaded: false,
        realAvailabilityLoaded: false,
        // Real per-variant, per-location quantities from the Storefront API.
        // Stored separately from productAvailabilities so it isn't wiped when
        // fetchVariantData() replaces the availability[] array for a variant.
        //   shape: { [variantId]: { [locationId]: qty, [locationName]: qty } }
        realStockMap: {},

        init() {
            this.productEl = this.$el;

            this.connectorPricing =
                this.getProductDataFromJSON(".productPriceStoreJSON") || [];

            this.productType =
                this.getProductDataFromJSON(".productTypeJSON") || {};

            this.productLocations =
                   this.getProductDataFromJSON(".productLocationsJSON") || [];
            this.productAvailabilities =
                this.getProductDataFromJSON(".productAvailabilityStoreJSON") || [];

            const selectedVariant = this.$el
                      .querySelector("[data-selected-variant]")
               ?.getAttribute("data-selected-variant");

                 this.initVariant(selectedVariant, true).then(() => {

                             this.syncWithStore(Alpine.store("selectedStore"));
                     });

            // Kick off real inventory fetch in parallel with the per-variant
            // section fetches. Once it resolves, recompute availability.
            this.fetchRealAvailability();

            document.addEventListener("store-changed", (event) => {
                const storeDetail = event.detail;
                const storeId = storeDetail.storeId;
                const storeName = storeDetail.storeTitle;

                const selectedVariant = this.$el
                    .querySelector("[data-selected-variant]")
                    .getAttribute("data-selected-variant");
                this.updatePrice(selectedVariant, storeId);

                    this.initVariant(selectedVariant, true).then(() => {
                                    const bestVariant = this.getBestVariantForStore(storeName);
                                this.updatePrice(bestVariant, storeId);
                                 this.updateLocationByAllVariants(storeName);
                            });

            });

            document.addEventListener("update-cart-items", (event) => {
                const items = event.detail.items;
                this.cartItemsHasBike(items);
            });

            this.$watch("productAvailable", (newProductAvailable) => {
                this.$dispatch("product-available-changed", {
                    productAvailable: newProductAvailable
                });
            });

            this.syncWithStore(Alpine.store('selectedStore'));

            this.$watch(
                () => Alpine.store('selectedStore'),
                (store) => this.syncWithStore(store)
            );

            setTimeout(() => {
                this.autoSelectFirstAvailableSize();
            }, 1400);
        },

        autoSelectFirstAvailableSize() {
            const sizeOptions = document.querySelectorAll(
                'input[type="radio"][data-option-name="Size"]'
            );
            
            const firstAvailableSize = Array.from(sizeOptions).find(option => 
                !option.classList.contains('is-disabled')
            );
            
            if (firstAvailableSize && !firstAvailableSize.checked) {
                firstAvailableSize.click();
            }
        },

        getBestVariantForStore(storeTitle) {
            const byStore = this.productAvailabilities.find(v =>
                v.availability.some(a =>
                    a.location_name === storeTitle && a.available
                )
            );
            if (byStore) return byStore.id;

            const byWarehouse = this.productAvailabilities.find(v =>
                v.availability.some(a =>
                    a.location_name === 'Warehouse' && a.available
                )
            );
            if (byWarehouse) return byWarehouse.id;

            return this.productAvailabilities[0]?.id;
        },

        syncWithStore(store) {
            if (!store || !store.id) return;

            const bestVariant = this.getBestVariantForStore(store.location_title);

            this.updatePrice(bestVariant, store.id);
            this.updateLocation(bestVariant, store.location_title);
        },

        async initVariant(selectedVariant, useAllVariants = false) {
            this.productLocations =
                this.getProductDataFromJSON(".productLocationsJSON") || [];
            this.productAvailabilities =
                this.getProductDataFromJSON(".productAvailabilityStoreJSON") ||
                [];

            // Reset availability loaded flag and apply cached UI state immediately
            this.availabilityLoaded = false;
            this.updateStyleOptions();

            const fetchPromises = this.productAvailabilities
                .filter((e) => e.id !== selectedVariant)
                .map(
                    async (e) =>
                        await this.fetchVariantData(
                            this.productAvailabilities[0].product_handle,
                            e.id
                        )
                );

            Promise.all(fetchPromises)
                .then(() => {
                    this.availabilityLoaded = true;
                    if (useAllVariants) {
                        this.updateLocationByAllVariants();
                    } else {
                        this.updateLocation(selectedVariant);
                    }
                    this.updatePrice(selectedVariant);
                    this.updateStyleOptions();
                })
                .catch((error) => {
                    console.error(
                        "Error in one of the fetch operations:",
                        error
                    );
                });
        },
        
        processAllVariants(selectedVariant) {
            this.initVariant(selectedVariant, true);
        },

        async fetchVariantData(productHandle, variantId) {
            this.load = false;
            await fetch(
                `/products/${productHandle}?variant=${variantId}&section_id=variant-data-handler`
            )
                .then((response) => {
                    if (!response.ok)
                        throw new Error(`Error ${response.status}`);
                    return response.text();
                })
                .then((html) => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, "text/html");

                    const newJsonElement =
                        doc.querySelector(".variantDataJSON");
                    if (newJsonElement) {
                        try {
                            const jsonData = JSON.parse(
                                newJsonElement.textContent.trim()
                            );

                            if (
                                Array.isArray(jsonData) &&
                                jsonData.length > 0
                            ) {
                                const availability = jsonData[0].availability;

                                const variant = this.productAvailabilities.find(
                                    (e) => e.id == variantId
                                );

                                if (variant) {
                                    variant.availability = availability;
                                }

                                setTimeout(() => {
                                    this.load = true;
                                }, 500);
                            } else {
                                console.error("No valid JSON data available");
                            }
                        } catch (error) {
                            console.error(error, newJsonElement.textContent);
                        }
                    } else {
                        console.error("variantDataJSON not found");
                    }
                })
                .catch((error) => console.error("error variant fetch", error));
        },

        updatePrice(selectedVariant, storeCode = window.OD.store.id) {
            if (!this.connectorPricing || !storeCode) return;
            const getPrice = this.getPriceByStore(selectedVariant, storeCode);

            if (getPrice?.price) {
                const isPLP = ['collection', 'search'].includes(window.themeVariables?.settings?.pageType);
                this.storePriceWithCompareAtPrice = isPLP
                    ? !!getPrice.anyVariantOnSale
                    : (getPrice.compareAtPrice !== null &&
                       getPrice.price !== null &&
                       getPrice.compareAtPrice > getPrice.price);
            
                if (this.storePriceWithCompareAtPrice) {
                    const savingsAmount = (
                        getPrice.compareAtPrice - getPrice.price
                    ).toFixed(2);
                    const savingsPercentage = (
                        ((getPrice.compareAtPrice - getPrice.price) /
                            getPrice.compareAtPrice) *
                        100
                    ).toFixed(0);
            
                    this.productBadges.discount = {
                        amount: `Save $${savingsAmount}`,
                        percentage: `Save ${savingsPercentage}%`
                    };
                }
            
                this.cartPrice = JSON.stringify(getPrice);
            
                this.storePrice = {
                    digit: {
                        price: getPrice.price,
                        compareAtPrice: getPrice.compareAtPrice
                    },
                    ...this.addDollarSignToPricesInPlace(getPrice)
                };
            } else {
                // No Fusion connector price for this store → do NOT flip
                // productAvailable to false. Stock is tracked independently of
                // connector pricing; price-list.liquid falls back to Shopify's
                // native product.price when storePrice?.price is empty.
                this.storePrice = {
                    price: " "
                };
                this.storePriceWithCompareAtPrice = false;
            }
        },

        updateStyleOptions() {
            const optionFields = this.productEl.querySelectorAll(
                ".variant-picker__option"
            );

            optionFields.forEach((field) => {
                const options = field.querySelectorAll("input[type='radio']");

                options.forEach((option) => {
                    const value = option.dataset.optionValue;
                    const label = field.querySelector(
                        `label[for='${option.id}']`
                    );

                    const storeKey = this.getCurrentStoreKey();
                    if (!this.optionAvailabilityCache[storeKey]) {
                        this.optionAvailabilityCache[storeKey] = {};
                    }

                    // If availability not fully loaded yet, use cached state.
                    if (!this.availabilityLoaded) {
                        const cached = this.optionAvailabilityCache[storeKey][value];
                        const shouldEnable = cached === true || option.checked;
                        if (shouldEnable) {
                            option.classList.remove("is-disabled");
                            label.classList.remove("is-disabled");
                        } else {
                            option.classList.add("is-disabled");
                            label.classList.add("is-disabled");
                        }
                        return;
                    }

                    const isAvailable = this.productAvailabilities.some(
                        (variant) =>
                            variant.options.includes(value) &&
                            variant.availability.some(
                                (avail) =>
                                    (avail.location_name === "Warehouse" ||
                                        avail.location_name ===
                                            this.storeLocation?.location
                                                ?.name) &&
                                    this.hasRealStockAt(avail, variant.id)
                            )
                    );

                    // Update cache for this store and option value
                    this.optionAvailabilityCache[storeKey][value] = isAvailable;

                    if (isAvailable) {
                        option.classList.remove("is-disabled");
                        label.classList.remove("is-disabled");
                    } else {
                        option.classList.add("is-disabled");
                        label.classList.add("is-disabled");
                    }
                });
            });
        },

        getCurrentStoreKey() {
            const store = window?.OD?.store;
            return String(store?.id || store?.location_title || "default");
        },

        updateLocation(variant, storeName = window.OD.store.location_title) {
            if (!storeName) return;

            const { matchingStoreLocation } =
                this.getLocationByStore(storeName);

            this.storeLocation = matchingStoreLocation || {};

            let variantAvailabilityArr = this.productAvailabilities.filter(
                (e) => e.id == variant
            );

            let storeAvailabilityObj =
                variantAvailabilityArr[0]?.availability.find(
                    (loc) =>
                        loc?.location_id == this.storeLocation?.location?.id
                );

            let warehouseAvailabilityObj =
                variantAvailabilityArr[0]?.availability.find(
                    (loc) => loc.location_name === "Warehouse"
                );

            // Use real per-location stock from Storefront API when available,
            // with a fallback to the embedded `available` flag.
            let storeAvailability = this.hasRealStockAt(
                storeAvailabilityObj,
                variant
            );

            let warehouseAvailability =
                warehouseAvailabilityObj?.location_name === "Warehouse" &&
                this.hasRealStockAt(warehouseAvailabilityObj, variant);

            this.storeLocation = {
                ...this.storeLocation,
                available: storeAvailability
            };

            this.productWarehouse = {
                ...this.productWarehouse,
                available: warehouseAvailability
            };

            this.productAvailable = storeAvailability || warehouseAvailability;
        },
        
        updateLocationByAllVariants(storeName = window.OD.store.location_title) {
            if (!storeName) return;
            
            const { matchingStoreLocation } = this.getLocationByStore(storeName);
            
            this.storeLocation = matchingStoreLocation || {};
            
            let isStoreAvailable = false;
            let isWarehouseAvailable = false;

            this.productAvailabilities.forEach((variant) => {
                let storeAvailabilityObj = variant.availability.find(
                    (loc) => loc?.location_id == this.storeLocation?.location?.id
                );
                
                let warehouseAvailabilityObj = variant.availability.find(
                    (loc) => loc.location_name === "Warehouse"
                );
                
                let storeAvailability = this.hasRealStockAt(
                    storeAvailabilityObj,
                    variant.id
                );

                let warehouseAvailability =
                    warehouseAvailabilityObj?.location_name === "Warehouse" &&
                    this.hasRealStockAt(
                        warehouseAvailabilityObj,
                        variant.id
                    );

                if (storeAvailability) isStoreAvailable = true;
                if (warehouseAvailability) isWarehouseAvailable = true;
            });
            
            this.storeLocation = {
                ...this.storeLocation,
                available: isStoreAvailable
            };
            
            this.productWarehouse = {
                ...this.productWarehouse,
                available: isWarehouseAvailable
            };

            this.productAvailable = isStoreAvailable || isWarehouseAvailable;
        },

        /**
         * Returns true only when the location has real, physical stock.
         *
         * Since March 2026 Shopify's `store_availability.available` flag means
         * "procurable for pickup here" (including via inter-store transfer) —
         * NOT "physically stocked here". So we consult realStockMap (populated
         * by fetchRealAvailability) which holds the per-location
         * `quantityAvailable` from the Storefront GraphQL API.
         */
        hasRealStockAt(locObj, variantId) {
            if (!locObj) return false;

            if (this.realAvailabilityLoaded && variantId != null) {
                const map = this.realStockMap[String(variantId)];
                if (map) {
                    let qty = map[String(locObj.location_id)];
                    if (typeof qty !== "number") qty = map[locObj.location_name];
                    if (typeof qty === "number") return qty > 0;
                }
            }

            // Fallback while Storefront fetch is pending or if it failed.
            return Boolean(locObj.available);
        },

        /**
         * Fetches real per-location `quantityAvailable` from the Shopify
         * Storefront GraphQL API and populates `realStockMap`. Safe to call
         * once per product page load.
         *
         * Requires the Storefront token to have BOTH scopes:
         *   - unauthenticated_read_product_pickup_locations
         *   - unauthenticated_read_product_inventory
         */
        async fetchRealAvailability() {
            const token = getStorefrontAccessToken();
            if (!token) {
                console.warn(
                    "[Storefront API] No token configured. Set it in " +
                        "Theme settings → Store Locator → Storefront API access token."
                );
                return;
            }
            const productHandle = this.productAvailabilities?.[0]?.product_handle;
            if (!productHandle) return;

            const query = `
                query ProductStoreAvailability($handle: String!) {
                    product(handle: $handle) {
                        variants(first: 100) {
                            nodes {
                                id
                                storeAvailability(first: 100) {
                                    nodes {
                                        available
                                        quantityAvailable
                                        location { id name }
                                    }
                                }
                            }
                        }
                    }
                }
            `;

            try {
                const response = await fetch(
                    `/api/${STOREFRONT_API_VERSION}/graphql.json`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Shopify-Storefront-Access-Token": token
                        },
                        body: JSON.stringify({
                            query,
                            variables: { handle: productHandle }
                        })
                    }
                );

                if (!response.ok) {
                    throw new Error(`Storefront API HTTP ${response.status}`);
                }
                const json = await response.json();
                if (json.errors) {
                    throw new Error(
                        "GraphQL errors: " + JSON.stringify(json.errors)
                    );
                }

                const variants = json?.data?.product?.variants?.nodes || [];
                if (variants.length === 0) return;

                const nextMap = {};
                variants.forEach((v) => {
                    const variantId = String(v.id).split("/").pop();
                    const perLocation = {};
                    (v.storeAvailability?.nodes || []).forEach((sa) => {
                        const locationId = String(sa.location.id)
                            .split("/")
                            .pop();
                        const locName = sa.location.name;
                        const qty =
                            typeof sa.quantityAvailable === "number"
                                ? sa.quantityAvailable
                                : null;
                        if (qty !== null) {
                            perLocation[locationId] = qty;
                            perLocation[locName] = qty;
                        }
                    });
                    nextMap[variantId] = perLocation;
                });
                this.realStockMap = nextMap;
                this.realAvailabilityLoaded = true;

                // Recompute display with real data now that it's available.
                this.updateLocationByAllVariants();
                this.updateStyleOptions();
            } catch (err) {
                console.warn(
                    "[Storefront API] Real availability fetch failed. " +
                        "Check that the token has scopes: " +
                        "unauthenticated_read_product_pickup_locations AND " +
                        "unauthenticated_read_product_inventory. " +
                        "Falling back to embedded availability flag.",
                    err
                );
            }
        },

        cartItemsHasBike(items) {
            const cartHasBike = items?.some(
                (el) => el.properties?._isBike === "true"
            );
            this.isBikeInCart = cartHasBike;
        },

        addDollarSignToPricesInPlace(priceObj) {
            for (const key in priceObj) {
                if (priceObj[key] !== null && !isNaN(priceObj[key])) {
                    priceObj[key] = `$${priceObj[key].toLocaleString("en-AU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}`;
                }
            }

            return priceObj;
        },

        getProductDataFromJSON(classJSON) {
            const scriptJSON =
                this.$el.querySelector(classJSON) ||
                this.productEl.querySelector(classJSON);

            if (scriptJSON) {
                try {
                    const dataOBJ = JSON.parse(scriptJSON.textContent);

                    if (
                        dataOBJ &&
                        (Array.isArray(dataOBJ) || typeof dataOBJ === "object")
                    ) {
                        return dataOBJ;
                    } else {
                        console.error(
                            `Invalid JSON structure: ${classJSON}`,
                            dataOBJ
                        );
                        return null;
                    }
                } catch (error) {
                    console.error(`Error parsing JSON: ${classJSON}`, error);
                    return null;
                }
            } else {
                console.error("No JSON found for class:", classJSON);
                return null;
            }
        },

        parsePrice(price) {
            if (price === '' || price === null || price === undefined) return null;
            const n = Number(price);
            return Number.isFinite(n) ? n : null;
        },

        getPriceByStore(selectedVariant, storeCode) {
            if (!this.connectorPricing) return;

            let minPrice = 999999;
            let maxPrice = 0;
            let compareAtPrice = null;
            let anyVariantOnSale = false;
            this.connectorPricing.forEach(variant => {
                const variantPrices = variant[Object.keys(variant)[0]];
                if (variantPrices) {
                  variantPrices.forEach(locationPrice => {
                      if (locationPrice.storeCode == storeCode) {
                        const storePrice = (locationPrice.price * 1)
                        if (storePrice < minPrice) minPrice = storePrice;
                        if (storePrice > maxPrice) maxPrice = storePrice;
                        if (locationPrice.compareAtPrice != '') {
                          compareAtPrice = locationPrice.compareAtPrice
                        }
                        // NEW: detect any discount across ANY variant for this store
                        const p = this.parsePrice(locationPrice.price);
                        const c = this.parsePrice(locationPrice.compareAtPrice ?? locationPrice.singleCompareAtPrice);
                        if (c !== null && p !== null && c > p) anyVariantOnSale = true;
                      }
                  });
                }
            });

            const matchingItemObj = this.connectorPricing.find(
                (item) => Object.keys(item)[0] == selectedVariant
            );
            const matchingItem = matchingItemObj
                ? Object.values(matchingItemObj)[0]
                : null;

            if (!matchingItem) {
                console.log("No item found with these Selected Variant");
                return null;
            }

            const priceObj = matchingItem.find(
                (price) => price.storeCode == storeCode
            ); 

            if (!priceObj) {
                console.log("Price not found for selected:", storeCode);
                return null;
            }

            const price = this.parsePrice(priceObj.price);
            const selectedCompareAt = this.parsePrice(
                priceObj.compareAtPrice ?? priceObj.singleCompareAtPrice
            );

            return {
                price,
                collectionMinPrice: minPrice,
                collectionMaxPrice: maxPrice,
                compareAtPrice: selectedCompareAt,
                anyVariantOnSale
            };
        },

        getLocationByStore(storeName) {
            if (!this.productLocations || !storeName) {
                console.warn("Product locations or store name is not defined.");
                return null;
            }

            const matchingStoreLocation = this.productLocations.find(
                (item) =>
                    item.location.name &&
                    (storeName.includes(item.location.name) ||
                        storeName == item.location.name)
            );

            const matchingProductWarehouse = this.productLocations.find(
                (item) => item.location.name === "Warehouse"
            );

            if (!matchingStoreLocation && !matchingProductWarehouse) {
                console.warn(
                    "No matching store or warehouse found for storeName:",
                    storeName
                );
                return null;
            }

            return { matchingStoreLocation, matchingProductWarehouse };
        }
    };
}